import { APIPresentationAssetAttributes } from '@ardoq/api-types';
import { MetaInfoViewStatus } from 'streams/views/mainContent/types';
import { isTruthy } from '@ardoq/common-helpers';
import {
  DeprecationPhasesInPresentationScope,
  OverallPresentationViewInfo,
  PresentationSlideViewInfo,
  SlidePermisionsInPresentationScope,
} from './types';
import { dateFromTimestamp } from '@ardoq/date-time';
import {
  type PermissionContext,
  presentationPermissionOperations,
} from '@ardoq/access-control';
import { t, type I18nLocale } from '@ardoq/i18n';

export const getMinimumSlideKillDateForPresentation = (
  deprecatedSlides: PresentationSlideViewInfo[]
) => {
  const killDates = deprecatedSlides
    .map(({ killDate }) => killDate)
    .filter(isTruthy);

  const minimumKillDate = killDates.length
    ? dateFromTimestamp(Math.min(...killDates.map(date => date.getTime())))
    : null;

  return minimumKillDate;
};

type getOveralDeprecationPhaseForPresentationArgs = {
  activePresentation: APIPresentationAssetAttributes;
  deprecatedSlidesInfo?: PresentationSlideViewInfo[];
  permissionContext: PermissionContext;
};
const getOverallPermissionsForPresentation = ({
  activePresentation,
  deprecatedSlidesInfo,
  permissionContext,
}: getOveralDeprecationPhaseForPresentationArgs) => {
  const { FULL_PERMISSIONS, LIMITED_PERMISSIONS, MIXED_PERMISSIONS } =
    SlidePermisionsInPresentationScope;

  const canEditAllDeprecatedSlides = deprecatedSlidesInfo?.every(
    slideInfo => slideInfo.hasFullAccess
  );

  const canEditPresentation =
    presentationPermissionOperations.canEditPresentation(
      permissionContext,
      activePresentation
    );

  if (deprecatedSlidesInfo?.length) {
    if (canEditPresentation && canEditAllDeprecatedSlides) {
      return FULL_PERMISSIONS;
    } else if (canEditPresentation && !canEditAllDeprecatedSlides) {
      return MIXED_PERMISSIONS;
    }

    return LIMITED_PERMISSIONS;
  }
};

const getDeprecationPhaseCountForSlides = (
  deprecatedSlidesInfo?: PresentationSlideViewInfo[]
) => {
  const result = {
    discontinued: 0,
    soonToBeDiscontinued: 0,
  };
  deprecatedSlidesInfo?.forEach(slideIfo => {
    if (slideIfo.viewStatus === MetaInfoViewStatus.DISCONTINUED) {
      result.discontinued++;
    } else if (
      slideIfo.viewStatus === MetaInfoViewStatus.SOON_TO_BE_DISCONTINUED
    ) {
      result.soonToBeDiscontinued++;
    }
  });
  return result;
};

const getOveralDeprecationPhaseForPresentation = (
  deprecatedSlidesInfo: PresentationSlideViewInfo[]
) => {
  const { DISCONTINUED_ONLY, SOON_TO_BE_DISCONTINUED_ONLY, MIXED_PHASES } =
    DeprecationPhasesInPresentationScope;

  const deprecationPhasesCount =
    getDeprecationPhaseCountForSlides(deprecatedSlidesInfo);

  if (deprecatedSlidesInfo.length) {
    if (deprecationPhasesCount.discontinued === deprecatedSlidesInfo.length) {
      return DISCONTINUED_ONLY;
    } else if (
      deprecationPhasesCount.soonToBeDiscontinued ===
      deprecatedSlidesInfo.length
    ) {
      return SOON_TO_BE_DISCONTINUED_ONLY;
    }
    return MIXED_PHASES;
  }
};

export const getOverallPresentationViewInfo = (
  activePresentation: APIPresentationAssetAttributes,
  deprecatedSlidesInfo: PresentationSlideViewInfo[],
  permissionContext: PermissionContext
) => {
  const result = {
    presentation: activePresentation,
    overallDeprecationPhase:
      getOveralDeprecationPhaseForPresentation(deprecatedSlidesInfo),
    overallPermissionsLevel: getOverallPermissionsForPresentation({
      deprecatedSlidesInfo,
      activePresentation,
      permissionContext,
    }),
  };
  return result;
};

type GetPhaseMessageTextArgs = {
  overallPresentationViewInfo?: OverallPresentationViewInfo;
  isKilldated: boolean;
  i18nLocale: I18nLocale;
};

export const getPhaseMessageText = ({
  overallPresentationViewInfo,
  isKilldated,
  i18nLocale,
}: GetPhaseMessageTextArgs) => {
  if (
    overallPresentationViewInfo?.overallDeprecationPhase ===
    DeprecationPhasesInPresentationScope.SOON_TO_BE_DISCONTINUED_ONLY
  ) {
    if (isKilldated) {
      return '';
    }
    return overallPresentationViewInfo.overallPermissionsLevel ===
      SlidePermisionsInPresentationScope.MIXED_PERMISSIONS
      ? t(
          i18nLocale,
          'viewDeprecation:phaseMessage.soonDiscontinuedMixed',
          "This presentation contains views that will soon be discontinued and you don't have access to migrate them all."
        )
      : t(
          i18nLocale,
          'viewDeprecation:phaseMessage.soonDiscontinued',
          'This presentation contains views that will soon be discontinued.'
        );
  } else if (
    overallPresentationViewInfo?.overallDeprecationPhase ===
    DeprecationPhasesInPresentationScope.DISCONTINUED_ONLY
  ) {
    return overallPresentationViewInfo.overallPermissionsLevel ===
      SlidePermisionsInPresentationScope.MIXED_PERMISSIONS
      ? t(
          i18nLocale,
          'viewDeprecation:phaseMessage.discontinuedMixed',
          "This presentation contains discontinued views and you don't have access to migrate them all."
        )
      : t(
          i18nLocale,
          'viewDeprecation:phaseMessage.discontinued',
          'This presentation contains discontinued views.'
        );
  } else if (
    overallPresentationViewInfo?.overallDeprecationPhase ===
    DeprecationPhasesInPresentationScope.MIXED_PHASES
  ) {
    return isKilldated
      ? t(
          i18nLocale,
          'viewDeprecation:phaseMessage.mixedKilldated',
          'Some views are discontinued and others are soon to be discontinued.'
        )
      : t(
          i18nLocale,
          'viewDeprecation:phaseMessage.mixed',
          'This presentation contains views that are discontinued and soon to be discontinued.'
        );
  }
};
